import { useCallback, useEffect, useState } from 'react';
import type { NoteCounts, UserNoteTypeII } from '@ts/types';
import {
  getDashboardCache,
  isDashboardCacheValid,
  setDashboardCache,
} from '@utils/notesCache';

import { NotesLoader } from '../../../../../../loaders/DashboardLoader';

/**
 * Hook for managing Notes to Review data fetching and state
 * Handles caching, loading states, and data refresh
 */
export const useNotesToReview = () => {
  const [notes, setNotes] = useState<UserNoteTypeII[] | undefined | null>();
  const [noteCounts, setNoteCounts] = useState<NoteCounts>();
  const [loading, setLoading] = useState(true);
  const [noNotesGenerated, setNoNotesGenerated] = useState(false);

  const fetchFreshData = useCallback(async () => {
    try {
      const { notes, totalCount } = await NotesLoader('reviewing', 20, 0);

      // Update data smoothly without clearing existing data first
      setNotes((prevNotes) => {
        // Only update if new data is different
        if (JSON.stringify(prevNotes) !== JSON.stringify(notes)) {
          return notes;
        }
        return prevNotes;
      });

      setNoteCounts((prevCounts) => {
        if (prevCounts?.for_review_notes_count !== totalCount) {
          return { for_review_notes_count: totalCount };
        }
        return prevCounts;
      });

      if (totalCount === 0) {
        setNoNotesGenerated(true);
      }

      // Update cache
      setDashboardCache({
        notes,
        totalCount,
        filters: {
          category: 'reviewing',
          limit: 20,
          offset: 0,
        },
        timestamp: Date.now(),
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchNotesAndCounts = useCallback(async () => {
    try {
      // Check cache first
      const cache = getDashboardCache();

      if (cache && isDashboardCacheValid(cache)) {
        // Use cached data without changing loading state
        setNotes(cache.notes);
        setNoteCounts({
          for_review_notes_count: cache.totalCount,
        });

        // Fetch fresh data in background
        fetchFreshData();
      } else {
        // No valid cache, fetch fresh data
        await fetchFreshData();
      }
    } catch (error) {
      console.error('Error fetching notes and counts:', error);
      setLoading(false);
    }
  }, [fetchFreshData]);

  useEffect(() => {
    fetchNotesAndCounts();
  }, [fetchNotesAndCounts]);

  return {
    notes,
    noteCounts,
    loading,
    refresh: fetchNotesAndCounts,
    noNotesGenerated,
  };
};